import request from 'utils/axios';
import { httpVerbs, TOKEN_KEY, AGENCY_MANAGEMENT_BASE_URL, PERSON_MANAGEMENT_BASE_URL } from 'configs';
import {
  SignInPayload,
  UserInfoResponse,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  Token,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  CountryListResponse,
  RegisterAgencyResponse,
  GenerateOtpPayload,
  GenerateOtpResponse,
  VerifyOtpPayload,
  VerifyOtpResponse,
  ResendOtpPayload,
  ResendOtpResponse,
  AcceptTermsAndConditionsResponse,
  SetNewPasswordResponse,
  sessionResponse,
  RequestPasswordResetResponse,
  SetNewPasswordResetPayload,
  SetNewPasswordResetResponse,
  RequestPasswordResetPayload,
  VerifyOtpPasswordResetPayload,
  VerifyOtpPasswordResetResponse,
  CountryRegistrationResponse,
  ContractResponse,
} from './types';
import { getFromLocal, removeFromLocal, saveToLocal } from 'utils/cache';
import { auth, provider, signInWithEmailAndPassword, signInWithPopup, OAuthProvider } from './firebaseConfig';
import { IRecordId } from 'interfaces/general';
import { useQuery } from '@tanstack/react-query';
import getAuthenticationAPIErrors from './AuthenticationAPIErrors';
import { Method } from 'axios';
import { enLocale } from 'locales/i18n';
import { LANGUAGE_STORAGE_KEY } from 'locales/i18n';

const AUTH_URL = `${AGENCY_MANAGEMENT_BASE_URL}/auth`;
const CURRENT_USER = `${AGENCY_MANAGEMENT_BASE_URL}/api/v1/agent/me`;
const FORGOT_PASSWORD = `${AUTH_URL}/forgot`;
const COUNTRY_LIST = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/housekeeping/countries`;
const REGISTER_AGENCY = `${AGENCY_MANAGEMENT_BASE_URL}/api/v1/agency`;
const GENERATE_OTP = `${AGENCY_MANAGEMENT_BASE_URL}/api/v1/agency/generate-otp`;
const VERIFY_OTP = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/accountActivation/activate`;
const RESEND_OTP = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/accountActivation/re-send`;
const TERMS_AND_CONDITIONS = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/users/update`;
const REGISTRATION_NUMBER = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/housekeeping/registrationNumberFormats`;
const CONTRACT_URL = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/terms/signup`;
const SET_NEW_PASSWORD = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/users/set-password`;
const START_SESSION = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/users/start-session`;
const END_SESSION = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/users/end-session`;

const PASSWORD_RESET = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/password-reset/request`;
const SET_NEW_PASSWORD_RESET = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/password-reset/set-new-password`;
const VERIFY_PASSWORD_RESET = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/password-reset/verify`;

type DataPromise<T> = Promise<{ data: T }>;

export const signIn = async (credentials: SignInPayload) => {
  const AuthenticationAPIErrors = getAuthenticationAPIErrors();
  const { email, password } = credentials;
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await user.getIdToken();
    const refreshToken = user.refreshToken;
    return { idToken, refreshToken, user };
  } catch (error) {
    if (error.code === 'auth/invalid-credential') {
      try {
        const url = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/users/log-failed-login/${email}`;
        await request({ url, method: httpVerbs.POST as Method, headerVariant: 'public' });
      } catch (error) {
        throw new Error(error.response.data.message);
      }
    }
    throw new Error(AuthenticationAPIErrors[error.code] || AuthenticationAPIErrors['auth/default']);
  }
};

export const signInWithMicrosoft = async () => {
  const result = await signInWithPopup(auth, provider);
  const credential = OAuthProvider.credentialFromResult(result);
  const accessToken = credential?.accessToken;
  const idToken = credential?.idToken;
  return { accessToken, idToken };
};

export const getUser = async (): Promise<UserInfoResponse> => {
  const url = `${CURRENT_USER}/${auth?.currentUser?.email}`;
  const { data } = await request({ url: url, method: 'GET' });
  return data;
};

export const useCurrentUserInfo = () => {
  return useQuery({
    queryKey: ['auth', 'user-info'],
    queryFn: getUser,
  });
};

export const forgotPassword = (data: ForgotPasswordPayload): DataPromise<ForgotPasswordResponse> => {
  return request({
    url: FORGOT_PASSWORD,
    method: 'POST',
    headerVariant: 'public',
    data,
  });
};

export const resetPassword = (data: ResetPasswordPayload): DataPromise<ResetPasswordResponse> => {
  const params: any = {
    url: FORGOT_PASSWORD,
    method: httpVerbs.POST,
    data: data,
  };
  return request(params);
};

export const getCountryList = (): Promise<CountryListResponse> => {
  return request({ url: COUNTRY_LIST, method: 'GET', headerVariant: 'public' });
};

export const getCountryRegistration = (id: IRecordId): Promise<CountryRegistrationResponse> => {
  const url = `${REGISTRATION_NUMBER}/${id}`;
  return request({ url, method: httpVerbs.GET as Method, headerVariant: 'public' });
};

export const registerAgency = (data: FormData): Promise<RegisterAgencyResponse> => {
  return request({
    url: REGISTER_AGENCY,
    method: 'POST',
    headerVariant: 'public',
    data,
    customHeaders: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const generateOtp = (data: GenerateOtpPayload): Promise<GenerateOtpResponse> => {
  return request({
    url: GENERATE_OTP,
    method: 'POST',
    headerVariant: 'public',
    data,
  });
};

export const verifyOtp = (data: VerifyOtpPayload): Promise<VerifyOtpResponse> => {
  return request({
    url: `${VERIFY_OTP}/${data.email}`,
    method: 'PUT',
    headerVariant: 'public',
    params: {
      OTP: data.otp,
    },
  });
};

export const resendOtp = ({ email, fullName }: ResendOtpPayload): Promise<ResendOtpResponse> => {
  return request({
    url: `${RESEND_OTP}/${email}`,
    method: 'POST',
    headerVariant: 'public',
    params: {
      fullName,
    },
  });
};

export const getContract = (): Promise<ContractResponse> => {
  const selectedLanguage = getFromLocal(LANGUAGE_STORAGE_KEY) || enLocale;
  return request({ url: `${CONTRACT_URL}/${selectedLanguage}`, method: 'GET', headerVariant: 'public' });
};

export const acceptTermsAndConditions = (
  email: string,
  termsAndConditionId: number,
): Promise<AcceptTermsAndConditionsResponse> => {
  return request({
    url: `${TERMS_AND_CONDITIONS}/${email}`,
    method: 'PATCH',
    headerVariant: 'public',
    data: { termsAndConditionId },
  });
};

export const setNewPassword = (payload: {
  password: string;
  token: string | null;
}): Promise<SetNewPasswordResponse> => {
  return request({
    url: `${SET_NEW_PASSWORD}`,
    method: httpVerbs.POST as Method,
    headerVariant: 'public',
    data: {
      password: payload.password,
      token: payload.token,
    },
  });
};

export const startSession = (isSSO: boolean = false): Promise<sessionResponse> => {
  return request({
    url: START_SESSION,
    method: 'POST',
    customHeaders: {
      'Is-Sso': isSSO.toString(),
    },
  });
};

export const endSession = (): Promise<sessionResponse> => {
  return request({
    url: END_SESSION,
    method: 'PUT',
  });
};

export const requestPasswordReset = (data: RequestPasswordResetPayload): Promise<RequestPasswordResetResponse> => {
  return request({
    url: PASSWORD_RESET,
    method: 'POST',
    headerVariant: 'public',
    data: data,
  });
};

export const setNewPasswordReset = (data: SetNewPasswordResetPayload): Promise<SetNewPasswordResetResponse> => {
  return request({
    url: `${SET_NEW_PASSWORD_RESET}`,
    method: 'POST',
    headerVariant: 'public',
    data: data,
  });
};

export const verifyOtpPasswordReset = (
  data: VerifyOtpPasswordResetPayload,
): Promise<VerifyOtpPasswordResetResponse> => {
  return request({
    url: `${VERIFY_PASSWORD_RESET}`,
    method: 'POST',
    headerVariant: 'public',
    data: data,
  });
};

export const saveTokenToLocal = (token: Token) => saveToLocal(TOKEN_KEY, token);
export const removeTokenFromLocal = () => removeFromLocal(TOKEN_KEY);
export const getTokenFromLocal = () => getFromLocal(TOKEN_KEY);
